import type { ServerResponse } from 'node:http';
import { ContentTypeIds, REDIS_HOMEPAGE_KEY, SUPPORTED_LOCALES, entryPages } from '@/constants';
import type { IPageSlug } from '@/interfaces';
import getConfig from 'next/config';
import type { NextRouter } from 'next/router';
import { getBlogPostPath } from './blogPage.utils';
import { getCountryBySlug } from './country.utils';

const seoPageSlugRegex = new RegExp(`^(${SUPPORTED_LOCALES.join('|')})/track-`);

/**
 * An SEO page is identified by its URL, not by a Contentful field: pages/[articleTypeSlug]/[slug].tsx
 * routes /<locale>/track-* to the SEO renderer purely on the slug. Robots meta and the sitemap both
 * derive it from this one function, so the two surfaces cannot disagree.
 */
export const checkIsSeoPageSlug = (slug: string | undefined): boolean => !!slug && seoPageSlugRegex.test(slug);

const PUBLIC_PAGE_CACHE_CONTROL = 'public, s-maxage=43200, stale-while-revalidate=60';

/**
 * Shared-cache header for the pages that render straight from Contentful without the Redis layer.
 * Call it only once the response is certain to be a 200 — a `notFound` or redirect returned later
 * inherits whatever was set, so a 404 would be offered to any shared cache for the full 12 hours.
 * Preview requests are skipped entirely so unpublished drafts can never enter a shared cache;
 * Next.js already sends `no-store` on a getServerSideProps response that sets nothing.
 */
export const setPublicPageCacheHeader = (res: ServerResponse, isPreviewMode: boolean) => {
  if (isPreviewMode) return;

  res.setHeader('Cache-Control', PUBLIC_PAGE_CACHE_CONTROL);
};

export const checkIsPreviewModeRequest = (query: NodeJS.Dict<string | string[]>): boolean => {
  const { serverRuntimeConfig } = getConfig();
  const isPreviewTokenCorrect = query?.previewToken === serverRuntimeConfig.previewModeAccessToken;
  const isPreviewMode = query?.preview === 'true' && isPreviewTokenCorrect;

  return isPreviewMode;
};

export const getCachePageSlugFromUrl = (type: ContentTypeIds, pageData: Record<string, any>): string => {
  if (!entryPages.includes(type)) return '';
  const SLASH = '/';

  // blog articles live under /blog/<slug>, unlike landing pages whose slug is the full path
  if (type === ContentTypeIds.blogPost) {
    return pageData.slug ? getBlogPostPath(pageData.slug) : '';
  }

  return pageData.slug ? SLASH + pageData.slug : REDIS_HOMEPAGE_KEY;
};

export const splitTradelanePageSlug = (slug: string) => {
  const TRADELANE_PAGE_PATTERN = /^([a-z-]+)-to-([a-z-]+)$/;
  const tradelanePageMatch = slug.match(TRADELANE_PAGE_PATTERN);

  if (!tradelanePageMatch) return null;

  const originSlug = tradelanePageMatch[1];
  const destinationSlug = tradelanePageMatch[2];

  const isOriginValid = getCountryBySlug(originSlug);
  const isDestinationValid = getCountryBySlug(destinationSlug);

  if (!isOriginValid || !isDestinationValid) return null;

  return { originSlug, destinationSlug };
};

/**
 * Checks if the current page path matches any of the provided page slugs
 *
 * @param pageSlugs - Array of page slug objects, each containing a description (slug)
 * @param asPath - Current page path from Next.js router
 * @returns boolean - True if current path matches any slug or if no slugs provided
 *
 * @example
 * // Homepage matches
 * checkPageSlugMatching([{ description: '/' }], '/') // true
 * checkPageSlugMatching([{ description: '/' }], '/?query=123') // true
 *
 * // Exact matches with query parameters
 * checkPageSlugMatching([{ description: '/page?id=1' }], '/page?id=1') // true
 * checkPageSlugMatching([{ description: '/page?id=1' }], '/page?id=2') // false
 *
 * // Path prefix matches
 * checkPageSlugMatching([{ description: '/blog' }], '/blog') // true
 * checkPageSlugMatching([{ description: '/blog' }], '/blog/post-1') // true
 * checkPageSlugMatching([{ description: '/blog' }], '/blog?category=news') // true
 * checkPageSlugMatching([{ description: '/blog' }], '/blogpost') // false
 * checkPageSlugMatching([{ description: '/blog/post1' }], '/blog') // false
 *
 * // Show on all pages when no slugs provided
 * checkPageSlugMatching([], '/any-path') // true
 * checkPageSlugMatching(null, '/any-path') // true
 */
export const checkPageSlugMatching = (pageSlugs: IPageSlug[] | undefined | null, asPath: NextRouter['asPath']) => {
  // If no page slugs defined, show on all pages
  if (!pageSlugs?.length) {
    return true;
  }

  // If all page slugs are empty, show on all pages
  if (pageSlugs?.every((pageSlug) => !pageSlug.description)) {
    return true;
  }

  // Check if current path matches any of the page slugs
  const pageMatch = pageSlugs.some(({ description: slug }: IPageSlug) => {
    // Homepage match: exact "/" or with query params "/?param=value"
    if (slug === '/' && (asPath === '/' || asPath.startsWith('/?'))) {
      return true;
    }

    // For other paths, check if current path starts with the slug
    if (slug !== '/' && slug?.includes('?')) {
      // Exact match required for slugs with query parameters
      return asPath === slug;
    }
    if (slug !== '/') {
      // For slugs without query parameters, check if path starts with slug
      // and is followed by end, slash, or question mark
      if (slug && asPath.startsWith(slug)) {
        const nextChar = asPath.charAt(slug.length);
        return nextChar === '' || nextChar === '/' || nextChar === '?';
      }
    }

    return false;
  });

  return pageMatch;
};
